import React, { useState } from 'react';
import { Container, Typography, CircularProgress, Alert } from '@mui/material';
import { useDashboard } from '../hooks/useDashboard';
import InventoryWarehouseStock from '../components/inventoryAnalytics/InventoryWarehouseStock';
import InventoryLowStock from '../components/inventoryAnalytics/InventoryLowStock';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useTranslation } from 'react-i18next';
import analyticsCache from '../utils/analyticsCache';

const InventoryAnalytics = () => {
    const { t } = useTranslation();
    const { getInventoryWarehouseStock, getInventoryLowStock } = useDashboard();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedWarehouseIds } = useWarehouseContext();

    const [data, setData] = useState({ warehouseStock: null, lowStock: null });

    const fetchingRef = React.useRef(false);

    const fetchData = React.useCallback(async (force = false) => {
        if (fetchingRef.current) return;

        const warehouseKey = selectedWarehouseIds?.length > 0 ? `_w${selectedWarehouseIds.join('_')}` : '';
        const cacheKey = `inventoryAnalytics_data${warehouseKey}`;
        const cachedData = analyticsCache.getItem(cacheKey);

        if (!force && cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                if (parsed) { setData(parsed); return; }
            } catch (e) {
                analyticsCache.removeItem(cacheKey);
            }
        }

        fetchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const [warehouseRes, lowStockRes] = await Promise.all([
                getInventoryWarehouseStock({}),
                getInventoryLowStock({})
            ]);

            const newError = warehouseRes.error || lowStockRes.error;
            if (newError) setError(newError);

            const newData = {
                warehouseStock: warehouseRes.data || null,
                lowStock: lowStockRes.data || null
            };

            setData(newData);
            analyticsCache.setItem(cacheKey, JSON.stringify(newData));
        } catch (err) {
            setError(err.message || t('hookErrors.analyticsPage.fetchFailed'));
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [getInventoryWarehouseStock, getInventoryLowStock, selectedWarehouseIds]);

    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWarehouseIds]);

    const strings = t('inventoryAnalytics.page', { returnObjects: true });

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <div style={{ marginBottom: 36 }}>
                <Typography
                    variant="h3"
                    component="h1"
                    fontWeight="800"
                    sx={{
                        background: 'linear-gradient(45deg, #05445E 30%, #189AB4 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    {strings.title}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {strings.subtitle}
                </Typography>
            </div>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {typeof error === 'object' ? JSON.stringify(error) : error}
                </Alert>
            )}

            {loading && !data.warehouseStock && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </div>
            )}

            {data.warehouseStock && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    {/* Warehouse Stock + Low Stock */}
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <div style={{ flex: '2 1 480px', minWidth: 340 }}>
                            <InventoryWarehouseStock data={data.warehouseStock} />
                        </div>
                        <div style={{ flex: '1 1 340px', minWidth: 300 }}>
                            <InventoryLowStock data={data.lowStock} />
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default InventoryAnalytics;
